import React, { useMemo, useState } from 'react';
import NavigationStepper, {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../../Components/Stepper/NavigationStepper';
import LaboratoryRequestCreateRequestHeaderForm, {
	LaboratoryRequestCreateDocumentInfo,
	LaboratoryRequestCreatePatient,
	LaboratoryRequestCreateRequestHeaderFormValues,
} from './LaboratoryRequestCreateRequestHeaderForm';
import {
	LaboratoryDataInput,
	useLaboratoryRequestCreateMutation,
} from '../../../../graphql/graphql';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';
import LaboratoryRequestCreateForm, {
	LaboratoryRequestFormDataScheme,
} from '../LaboratoryRequestCreate/LaboratoryRequestCreateForm';
import LaboratoryRequestCreateRecapitulation, {
	LaboratoryRequestCreateRecapitulationFormValues,
} from './LaboratoryRequestCreateRecapitulation';
import LaboratoryRequestCreateWizardStepWrapper from './LaboratoryRequestCreateWizardStepWrapper';
import useFilters from '../../../Base/Hooks/useFilters';
import OverlayLoading from '../../../../Components/OverlayLoading';
import { SELF_PAYER_VALUE } from '../../../Term/Components/HealthInsuranceCompanySelect/HealthInsuranceCompanySelect';

interface Props {
	onCreate: () => void;
	formId: string;
	patientId: string;
	workplaceId: string;
	laboratoryCode: string;
}

interface DataScheme {
	patient?: LaboratoryRequestCreatePatient;
	documentInfo?: LaboratoryRequestCreateDocumentInfo;
	data?: LaboratoryRequestFormDataScheme[];
	saveToECard?: boolean;
	electronicSignature?: boolean;
	electronicSignatureTimestamp?: boolean;
}

const LaboratoryRequestCreateWizard: React.FC<Props> = ({
	formId,
	patientId,
	onCreate,
	workplaceId,
	laboratoryCode,
}) => {
	const isUnilabs = laboratoryCode === 'unilabs';
	const [createRequest] = useLaboratoryRequestCreateMutation();
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const [data, setData] = useState<DataScheme>({});
	const {
		handleDateValue,
		handleTimeValue,
		checkEmptyString,
		handleSelect,
		handleFloat,
	} = useFilters();
	const [isLoading, setIsLoading] = useState(false);

	const handleSendRequest = async (data: DataScheme) => {
		setIsLoading(true);

		if (data.patient && data.data && data.documentInfo) {
			let insuranceCompanyTermItemId = undefined;
			if (!isUnilabs && data.documentInfo?.selfPaid !== true) {
				insuranceCompanyTermItemId = handleSelect(
					data.patient?.insuranceCompany,
				);
			}

			const response = await createRequest({
				input: {
					requisitionId: formId,
					userId: patientId,
					workplaceId: workplaceId,
					document: {
						date: handleDateValue(data.documentInfo?.date),
						time: handleTimeValue(data.documentInfo?.time),
						primarySampleType: data.documentInfo?.primarySampleType,
						selfPaid: data.documentInfo?.selfPaid,
						statim: data.documentInfo?.statim,
						statimPhoneNumber: data.documentInfo?.statimPhoneNumber,
						sampleTakenBy: checkEmptyString(
							data.documentInfo?.tookBy?.label ??
								data.documentInfo?.tookByCustom,
						),
						note: data.documentInfo?.note,
					},
					patient: {
						diagnosisTermItemValue: handleSelect(data.patient.diagnosis) ?? '',
						genderTermItemId: handleSelect(data.patient?.gender) ?? '',
						insuranceCompanyTermItemId,
						address: data.patient?.address,
						birthDate: handleDateValue(data.patient?.birthDate),
						insuranceNumber: data?.patient?.insuranceNumber,
						lastName: data?.patient?.lastName,
						firstName: data?.patient?.firstName,
						contactEmail: data?.patient?.contactEmail,
						contactPhoneNumber: data?.patient?.contactPhoneNumber,
						height: handleFloat(data?.patient?.height),
						weight: handleFloat(data?.patient?.weight),
					},
					data: data.data?.map(item => {
						return {
							wizardItemId: item.wizardItemId,
							value: item.value,
						} as LaboratoryDataInput;
					}),
					addToPatientECard: data.saveToECard ?? false,
					electronicSignature: {
						electronicSignature: data.electronicSignature ?? false,
						electronicSignatureTimestamp:
							data.electronicSignatureTimestamp ?? false,
					},
				},
			}).unwrap();
			if (response.saveLaboratoryRequisitionResult?.id) {
				showNotificationSuccess({
					content: t('component.laboratoryRequestCreate.requestCreated'),
				});
				onCreate();
			}
		}
		setIsLoading(false);
	};

	const handleOnPatientDataSuccess = (
		params: NavigationStepperComponentParams,
		values: LaboratoryRequestCreateRequestHeaderFormValues,
	) => {
		const { patient, documentInfo } = values;

		if (isUnilabs) {
			documentInfo.selfPaid = true;
		} else {
			documentInfo.selfPaid =
				patient.insuranceCompany?.value === SELF_PAYER_VALUE;
		}

		setData({
			patient,
			documentInfo,
			data: data?.data,
		});
		params.setActiveStepPath('requestData');
	};

	const handleOnRequestDataSuccess = (
		params: NavigationStepperComponentParams,
		requestData: LaboratoryRequestFormDataScheme[],
	) => {
		setData({
			patient: data?.patient,
			documentInfo: data?.documentInfo,
			data: requestData,
		});
		params.setActiveStepPath('recapitulation');
	};

	const handleConfirm = async (
		values: LaboratoryRequestCreateRecapitulationFormValues,
	) => {
		await handleSendRequest({ ...data, ...values });
	};

	const steps: NavigationStepperItemType[] = useMemo(() => {
		return [
			{
				name: 'component.laboratoryRequestCreateWizard.patientData',
				key: 'patientData',
				component: (params: NavigationStepperComponentParams) => (
					<LaboratoryRequestCreateWizardStepWrapper>
						<LaboratoryRequestCreateRequestHeaderForm
							onSuccess={values => handleOnPatientDataSuccess(params, values)}
							patientId={patientId}
							laboratoryCode={laboratoryCode}
							initialValues={
								data?.patient && data?.documentInfo
									? { patient: data.patient, documentInfo: data.documentInfo }
									: undefined
							}
							requisitionId={formId}
						/>
					</LaboratoryRequestCreateWizardStepWrapper>
				),
			},
			{
				name: 'component.laboratoryRequestCreateWizard.requestData',
				key: 'requestData',
				component: (params: NavigationStepperComponentParams) => (
					<LaboratoryRequestCreateWizardStepWrapper>
						<LaboratoryRequestCreateForm
							onSuccess={values => handleOnRequestDataSuccess(params, values)}
							formId={formId}
							initialData={data?.data}
							templatesEnabled
							workplaceId={workplaceId}
						/>
					</LaboratoryRequestCreateWizardStepWrapper>
				),
			},
			{
				name: 'component.laboratoryRequestCreateWizard.recapitulation',
				key: 'recapitulation',
				component: (params: NavigationStepperComponentParams) => (
					<LaboratoryRequestCreateWizardStepWrapper>
						<LaboratoryRequestCreateRecapitulation
							onConfirm={handleConfirm}
							data={data}
							patientId={patientId}
						/>
					</LaboratoryRequestCreateWizardStepWrapper>
				),
			},
		];
	}, [data]);

	return (
		<>
			<NavigationStepper
				steps={steps}
				initialStepPath='patientData'
				canSkip={false}
			/>
			{isLoading && <OverlayLoading />}
		</>
	);
};

export default LaboratoryRequestCreateWizard;
